import { resolveMarketingContext, marketingQuery, marketingFields, promotionSelection, promotionBookingPayload, requestPromotionQuote, applyPromotionQuote } from '@/services/transportMarketingClient';

const pendingRequests = new Map();
let leaveWarningRegistered = false;

export default {
  namespaced: true,
  state: () => ({ context: null, query: {}, status: 'idle', quoteStatus: 'idle', quote: null, fingerprint: '', requestId: 0, error: '', consent: false, paymentQuote: null, paymentFingerprint: '', paymentActive: false, pendingPaidReservation: null, pendingPaymentAttempt: null }),
  mutations: {
    update(state, payload) { Object.assign(state, payload); },
  },
  getters: {
    hasPromotion: state => Boolean(state.query.promo || state.query.invalid),
    fields: state => marketingFields(state.context, { consent: state.consent }),
  },
  actions: {
    async initialize({ state, commit, rootGetters }, query) {
      if (!leaveWarningRegistered) {
        window.addEventListener('beforeunload', event => {
          if (state.paymentActive || state.pendingPaidReservation || state.pendingPaymentAttempt) { event.preventDefault(); event.returnValue = ''; }
        });
        leaveWarningRegistered = true;
      }
      if (state.pendingPaidReservation || state.pendingPaymentAttempt) return;
      const companyId = Number(rootGetters.getCompanyData?.data?.company_id);
      const parsed = marketingQuery(query);
      if (state.context?.companyId === companyId && JSON.stringify(parsed) === JSON.stringify(state.query)) return;
      const requestId = state.requestId + 1;
      commit('update', { context: null, query: parsed, quote: null, fingerprint: '', quoteStatus: 'idle', requestId, status: 'loading', error: '', consent: false, paymentQuote: null, paymentFingerprint: '', paymentActive: false });
      try {
        const context = await resolveMarketingContext(process.env.VUE_APP_BASEURL, companyId, query);
        if (state.requestId !== requestId) return;
        commit('update', { context, status: 'ready' });
      } catch (error) {
        if (state.requestId === requestId) commit('update', { status: 'error', error: error.message });
      }
    },
    async quote({ state, commit, rootGetters }, { force = false } = {}) {
      if ((state.paymentActive || state.pendingPaidReservation || state.pendingPaymentAttempt) && state.paymentQuote) return state.paymentQuote;
      if (!state.query.promo && !state.query.invalid) return null;
      if (state.status !== 'ready' || !state.context?.promotion) throw new Error('Promotion unavailable.');
      const current = rootGetters['AptReservationsStore/getAptReservations'];
      const booking = promotionBookingPayload({ ...current, ...marketingFields(state.context) }, state.context.companyId);
      const fingerprint = promotionSelection(booking);
      if (!force && state.quote && state.fingerprint === fingerprint && Date.parse(state.quote.expires_at) > Date.now() + 10000) {
        commit('AptReservationsStore/SET_APT_RESERVATIONS', applyPromotionQuote(current, state.quote, rootGetters.getCompanyData.data), { root: true });
        return state.quote;
      }
      const requestId = state.quoteStatus === 'loading' && state.pendingFingerprint === fingerprint ? state.requestId : state.requestId + 1;
      commit('update', { requestId, pendingFingerprint: fingerprint, quoteStatus: 'loading', quote: null, fingerprint: '', error: '' });
      commit('AptReservationsStore/SET_APT_RESERVATIONS', { promotion_quote_token: null, promotion_quote: null }, { root: true });
      try {
        const key = `${state.context.companyId}:${state.context.query.promo}:${fingerprint}`;
        if (!pendingRequests.has(key)) {
          const request = requestPromotionQuote(process.env.VUE_APP_BASEURL, state.context, booking);
          pendingRequests.set(key, request);
          request.then(() => pendingRequests.delete(key), () => pendingRequests.delete(key));
        }
        const quote = await pendingRequests.get(key);
        const latest = promotionBookingPayload(rootGetters['AptReservationsStore/getAptReservations'], state.context.companyId);
        if (state.requestId !== requestId || promotionSelection(latest) !== fingerprint) throw new Error('Booking selection changed.');
        commit('AptReservationsStore/SET_APT_RESERVATIONS', { ...applyPromotionQuote(rootGetters['AptReservationsStore/getAptReservations'], quote, rootGetters.getCompanyData.data), ...marketingFields(state.context) }, { root: true });
        commit('update', { quote, fingerprint, quoteStatus: 'ready' });
        return quote;
      } catch (error) {
        if (state.requestId === requestId) commit('update', { quote: null, fingerprint: '', quoteStatus: 'error', error: error.message });
        throw error;
      }
    },
    discard({ state, commit }) {
      if (state.pendingPaidReservation || state.pendingPaymentAttempt) return;
      commit('update', { context: null, query: {}, status: 'idle', quoteStatus: 'idle', quote: null, fingerprint: '', error: '', requestId: state.requestId + 1 });
      commit('AptReservationsStore/SET_APT_RESERVATIONS', { promotion_token: null, promotion_quote_token: null, promotion_quote: null, promoCode: '', discount_amount: 0, couponApplied: false, couponAlreadyApplied: 0 }, { root: true });
      commit('setCurrentStep', 2, { root: true });
    },
  },
};
